import React, { memo, useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import CheckAndReturn from "../../../../utility/CheckAndReturn";
import {
  Grid,
  Stack,
  TextField,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ClearIcon from "@mui/icons-material/Clear";
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#4CB5F5", //theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

//FUNCTION
const OutstandingCustomerTable = ({ outstandingCustomerData }) => {
  //#region code
  const navigate = useNavigate();
  const [invoiceAmount, setInvoiceAmount] = useState(0);
  const [receiptAmount, setReceiptAmount] = useState(0);
  const [paymentAmount, setPaymentAmount] = useState(0);
  // console.log("outstandingCustomerData", outstandingCustomerData);
  // sorting implements
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [typeFilter, setTypeFilter] = useState(""); // "" means no filter, show all types

  const sortedData = useMemo(() => {
    if (
      !outstandingCustomerData?.ledger ||
      !Array.isArray(outstandingCustomerData.ledger)
    ) {
      return { ...outstandingCustomerData, ledger: [] };
    }

    let filteredLedger = [...outstandingCustomerData.ledger];

    // Filter by date range
    if (startDate || endDate) {
      filteredLedger = filteredLedger.filter((entry) => {
        const entryDate = new Date(entry.date);
        const isAfterStart = startDate
          ? entryDate >= new Date(startDate)
          : true;
        const isBeforeEnd = endDate ? entryDate <= new Date(endDate) : true;
        return isAfterStart && isBeforeEnd;
      });
    }

    // Filter by type
    if (typeFilter) {
      filteredLedger = filteredLedger.filter(
        (entry) => entry.type === typeFilter
      );
    }

    // Sort
    const sortedLedger = filteredLedger.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === "date") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (sortConfig.key === "amount") {
        aValue = Number(aValue);
        bValue = Number(bValue);
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return { ...outstandingCustomerData, ledger: sortedLedger };
  }, [outstandingCustomerData, sortConfig, startDate, endDate, typeFilter]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  useEffect(() => {
    let totalInvoiceAmount = 0;
    let totalReceiptAmount = 0;
    let totalPaymentAmount = 0;

    if (sortedData !== null && Array.isArray(sortedData.ledger)) {
      sortedData.ledger.forEach((row) => {
        if (row.type === "sale") {
          totalInvoiceAmount += Number(row.amount ?? 0);
        } else if (Boolean(row.is_credit) === false) {
          totalReceiptAmount += Number(row.amount ?? 0);
        } else if (Boolean(row.is_credit) === true) {
          totalPaymentAmount += Number(row.amount ?? 0);
        }
      });

      setInvoiceAmount(totalInvoiceAmount);
      setReceiptAmount(totalReceiptAmount);
      setPaymentAmount(totalPaymentAmount);
    }

    return () => {
      setInvoiceAmount(0);
      setReceiptAmount(0);
      setPaymentAmount(0);
    };
  }, [sortedData]);

  const LinkTableCell = styled(StyledTableCell)({
    cursor: "pointer",
    textDecoration: "none",
    "&:hover": {
      color: "darkblue",
      textDecoration: "underline",
    },
  });

  const handleInvoiceClick = (params) => {
    try {
      const data = params;
      navigate("/invoice", { state: { invoiceData: data } });
    } catch (e) {
      console.log(e);
    }
  };

  const handleVoucherClick = (params) => {
    try {
      if (params.type === "receipt" || params.type === "payment") {
        navigate("/transaction/create-vouchers", {
          state: { entity_number: params.entity_number, entity: "customer" },
        });
      } else {
        navigate("/transaction/journal-entry", {
          state: { entity_number: params.entity_number, entity: "customer" },
        });
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleCreditDebit = (params) => {
    try {
      if (Boolean(params.is_credit) === false) {
        return <span style={{ color: "green" }}>Cr</span>;
      } else if (Boolean(params.is_credit) === true) {
        return <span style={{ color: "red" }}>Dr</span>;
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleClearFilters = () => {
    setSortConfig({ key: "date", direction: "asc" });
    setStartDate(null);
    setEndDate(null);
    setTypeFilter("");
  };

  //#endregion

  return (
    <>
      <Box flex={1} textAlign="center">
        <Typography
          variant="h5"
          color="#1e61b4"
          fontSize={{ xs: "16px", md: "22px" }}
          fontWeight={700}
        >
          {outstandingCustomerData?.name ?? ""}
        </Typography>
        <Typography
          variant="subtitle"
          color={"grey"}
          fontSize={{ xs: "14px", md: "16px" }}
          fontWeight={500}
        >
          {CheckAndReturn.capitalizeWords(
            outstandingCustomerData?.address ?? ""
          )}
        </Typography>
      </Box>
      {outstandingCustomerData !== null &&
        outstandingCustomerData.customer !== undefined &&
        outstandingCustomerData.customer !== null && (
          <Grid container alignItems={"center"} spacing={1} my={2}>
            <Grid item xs={12} sm={3} md={3} columnSpacing={0}>
              <TextField
                readonly
                fullWidth
                size="small"
                label={
                  <span>
                    Firm Name<span style={{ color: "red" }}>*</span>
                  </span>
                }
                name="fname"
                value={outstandingCustomerData.customer.fname || ""}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={3} columnSpacing={0}>
              <TextField
                readonly
                fullWidth
                size="small"
                label={
                  <span>
                    Contact Name<span style={{ color: "red" }}>*</span>
                  </span>
                }
                name="contact_name"
                value={outstandingCustomerData.customer.contact_name || ""}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={3} columnSpacing={0}>
              <TextField
                readonly
                fullWidth
                size="small"
                label={
                  <span>
                    Mobile Number<span style={{ color: "red" }}>*</span>
                  </span>
                }
                name="mobile"
                type="number"
                value={outstandingCustomerData.customer.mobile || ""}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>
            {outstandingCustomerData.customer.email && (
              <Grid item xs={12} sm={3} md={3} columnSpacing={0}>
                <TextField
                  readonly
                  fullWidth
                  size="small"
                  label={<span>Email</span>}
                  name="email"
                  value={outstandingCustomerData.customer.email || ""}
                  sx={{ backgroundColor: "white" }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={3} md={3} columnSpacing={0}>
              <TextField
                readonly
                fullWidth
                size="small"
                label={
                  <span>
                    Address<span style={{ color: "red" }}>*</span>
                  </span>
                }
                name="address"
                value={outstandingCustomerData.customer.address || ""}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={3} columnSpacing={0}>
              <TextField
                readonly
                fullWidth
                size="small"
                label={
                  <span>
                    Country<span style={{ color: "red" }}>*</span>
                  </span>
                }
                name="country"
                value={outstandingCustomerData.customer.country || ""}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={3} columnSpacing={0}>
              <TextField
                readonly
                fullWidth
                size="small"
                label={
                  <span>
                    State<span style={{ color: "red" }}>*</span>
                  </span>
                }
                name="State"
                value={outstandingCustomerData.customer.State || ""}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>
            {outstandingCustomerData.customer.Pin && (
              <Grid item xs={12} sm={3} md={3} columnSpacing={0}>
                <TextField
                  readonly
                  fullWidth
                  type="number"
                  size="small"
                  label={<span>Pin</span>}
                  name="Pin"
                  value={outstandingCustomerData.customer.Pin || ""}
                  sx={{ backgroundColor: "white" }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={3} md={3} columnSpacing={0}>
              <TextField
                readonly
                fullWidth
                type="text"
                size="small"
                label={
                  <span>
                    Customer Type<span style={{ color: "red" }}>*</span>
                  </span>
                }
                name="type"
                value={outstandingCustomerData.customer.type || ""}
                defaultValue={0}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>
            {outstandingCustomerData.customer.pan_no && (
              <Grid item xs={12} sm={3} md={3} columnSpacing={0}>
                <TextField
                  readonly
                  fullWidth
                  size="small"
                  label="PAN/NO"
                  name="pan_no"
                  value={outstandingCustomerData.customer.pan_no || ""}
                  sx={{ backgroundColor: "white" }}
                />
              </Grid>
            )}
            {outstandingCustomerData.customer.gstin && (
              <Grid item xs={12} sm={3} md={3} columnSpacing={0}>
                <TextField
                  readonly
                  fullWidth
                  size="small"
                  label="GSTIN/UIN"
                  name="gstin"
                  value={outstandingCustomerData.customer.gstin || ""}
                  sx={{ backgroundColor: "white" }}
                />
              </Grid>
            )}
            {/* {
            outstandingCustomerData.customer.is_credit_days &&
            <Grid item xs={12} sm={3} md={3} columnSpacing={0}>
              <FormControlLabel
                readonly
                control={<Checkbox />}
                name="is_credit_days"
                checked={Boolean(outstandingCustomerData.customer.is_credit_days)}
                label="Check credit days"
              />
            </Grid>
          } */}
            {outstandingCustomerData.customer.credit_period && (
              <Grid item xs={12} sm={3} md={3} columnSpacing={0}>
                <TextField
                  readonly
                  fullWidth
                  label="Credit Period"
                  size="small"
                  type="number"
                  name="credit_period"
                  InputProps={{
                    endAdornment: "d",
                  }}
                  value={outstandingCustomerData.customer.credit_period || ""}
                  sx={{ backgroundColor: "white" }}
                />
              </Grid>
            )}
            {outstandingCustomerData.customer.credit_limit && (
              <Grid item xs={12} sm={3} md={3} columnSpacing={0}>
                <TextField
                  readonly
                  fullWidth
                  label="Credit Limit"
                  size="small"
                  type="number"
                  name="credit_limit"
                  value={outstandingCustomerData.customer.credit_limit || ""}
                  sx={{ backgroundColor: "white" }}
                />
              </Grid>
            )}
          </Grid>
        )}

      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="flex-end"
        mb={2}
      >
        <Grid item>
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={startDate || ""}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>

        <Grid item>
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={endDate || ""}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={3} md={3}>
          <TextField
            select
            size="small"
            label="Filter by Type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}

            fullWidth
          >
            <option value="">All</option>
            <option value="sale">Sale</option>
            <option value="receipt">Receipt</option>
            <option value="salesreturn">Sales Return</option>
            <option value="journal">Journal</option>
            <option value="payment">Payment</option>
            {/* Add other types if needed */}
          </TextField>
        </Grid>

        <Grid item>
          <IconButton
            onClick={() => {
              handleClearFilters();
            }}
            color="secondary"
            size="medium"
            aria-label="clear filters"
            sx={{ border: "1px solid #ccc" }}
          >
            <ClearIcon />
          </IconButton>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Box sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 700 }} aria-label="customized table">
            <TableHead>
              <TableRow>
                {[
                  { key: "sno", label: "S.No" },
                  { key: "date", label: "Date" },
                  { key: "type", label: "Type" },
                  { key: "amount", label: "Amount" },
                ].map((column) => (
                  <StyledTableCell
                    key={column.key}
                    align="left"
                    onClick={() => handleSort(column.key)}
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      {column.label}
                      {sortConfig.key === column.key &&
                        (sortConfig.direction === "asc" ? "↑" : "↓")}
                    </Box>
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedData !== null ? (
                sortedData.ledger.map((row, index) => (
                  <StyledTableRow key={row.id}>
                    <StyledTableCell align="left">{index + 1}</StyledTableCell>
                    <StyledTableCell align="left">
                      {CheckAndReturn.convertDateFormat(
                        row.type === "sale" ? row.date ?? "" : row.date ?? ""
                      )}
                    </StyledTableCell>
                    {row.type === "sale" ? (
                      <LinkTableCell
                        align="left"
                        onClick={() => handleInvoiceClick(row.entity_number)}
                      >
                        {`Bill to(${row.entity_number ?? ""})`}
                      </LinkTableCell>
                    ) : row.type === "salesreturn" ? (
                      <LinkTableCell
                        align="left"
                        // onClick={() => handleVoucherClick(row)}
                      >
                        {`Return from(${row.entity_number ?? ""})`}
                      </LinkTableCell>
                    ) : (
                      <LinkTableCell
                        align="left"
                        onClick={() => handleVoucherClick(row)}
                      >
                        {row.type === "payment"
                          ? `${row.type}(${row.entity_number ?? ""})`
                          : `${row.type} from(${row.entity_number ?? ""})`}
                      </LinkTableCell>
                    )}

                    <StyledTableCell align="left">
                      ₹{CheckAndReturn.roundToInteger(row.amount ?? 0)}{" "}
                      {handleCreditDebit(row)}
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={12}
                    sx={{ textAlign: "center", color: "red", fontSize: "16px" }}
                  >
                    <div>
                      <p>
                        <b>No Data Found.</b>
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </TableContainer>

      <Grid container spacing={1} mt={2}>
        <Grid container item xs={12} md={3} justifyContent={"center"}>
          <Stack direction={"row"}>
            <Typography
              variant="body1"
              sx={{ fontSize: "16px", fontWeight: 500 }}
            >
              Invoice:
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: "16px", fontWeight: 500, color: "green" }}
            >
              ₹{CheckAndReturn.roundToInteger(invoiceAmount ?? 0)}
            </Typography>
          </Stack>
        </Grid>
        <Grid container item xs={12} md={3} justifyContent={"center"}>
          <Stack direction={"row"}>
            <Typography
              variant="body1"
              sx={{ fontSize: "16px", fontWeight: 500 }}
            >
              Payment:
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: "16px", fontWeight: 500, color: "green" }}
            >
              ₹{CheckAndReturn.roundToInteger(paymentAmount ?? 0)}
            </Typography>
          </Stack>
        </Grid>
        <Grid container item xs={12} md={3} justifyContent={"center"}>
          <Stack direction={"row"}>
            <Typography
              variant="body1"
              sx={{ fontSize: "16px", fontWeight: 500 }}
            >
              Receipt:
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: "16px", fontWeight: 500, color: "green" }}
            >
              ₹{CheckAndReturn.roundToInteger(receiptAmount ?? 0)}
            </Typography>
          </Stack>
        </Grid>
        <Grid container item xs={12} md={3} justifyContent={"center"}>
          <Stack direction={"row"}>
            <Typography
              variant="body1"
              sx={{ fontSize: "16px", fontWeight: 500 }}
            >
              Dues:
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: "16px", fontWeight: 500, color: "green" }}
            >
              ₹
              {CheckAndReturn.roundToInteger(
                (invoiceAmount ?? 0) +
                  (paymentAmount ?? 0) -
                  (receiptAmount ?? 0)
              )}
            </Typography>
          </Stack>
        </Grid>
      </Grid>
    </>
  );
};

export default memo(OutstandingCustomerTable);
